import { useState, useEffect, useContext } from 'react';
import {} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonFab,
    SttChipUser,
    SttTable,
    SttTopControls,
    SttPage,
} from '../../../sporttia/all';
import { useToggle, useTuples, useCrud } from '../../../lib/hooks';
import { getPath } from '../../Pages';
import { copyToClipboard, fullName } from '../../../lib/utils';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type Preset = {
    isActive?: boolean | string;
    onTime?: boolean | string;
};

type BookingSchedule = {
    id: string;
    name: string;
    user?: {
        name?: string;
        surname?: string;
    };
    status?: string;
    dateini?: string;
    dateend?: string;
    facility?: unknown;
    numPeriods?: number;
};

export default function BookingSchedules(): React.ReactElement {
    // Filter presets, i.e. filter combinations handled by a single <Select>
    const presets: Record<string, Preset> = {
        ALL: {
            isActive: '',
            onTime: '',
        },
        ACTIVE: {
            isActive: true,
        },
        ONTIME: {
            onTime: true,
        },
    };

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [bookingSchedules, setBookingSchedules, , Get] = useCrud<any>();
    // Filters hook
    const [filters, setFilter] = useTuples(
        JSON.parse(
            sessionStorage.getItem('bookingSchedulesFilters') || 'null',
        ) || {
            name: '',
            dni: '',
            dateini: new DateBuilder().subYears(1).ymd(),
            dateend: new DateBuilder().ymd(),
        },
    );
    const [filterByPeriodEnabled, setFilterByPeriodEnabled] = useState<boolean>(
        sessionStorage.getItem('bookingSchedulesFilterByPeriodEnabled') ===
            'true',
    );
    const [preset, setPreset] = useState<string>(
        sessionStorage.getItem('bookingSchedulesPreset') || 'ACTIVE',
    );

    // Guardar filtros y preset en sessionStorage cuando cambian
    useEffect(() => {
        sessionStorage.setItem(
            'bookingSchedulesFilters',
            JSON.stringify(filters),
        );
    }, [filters]);

    useEffect(() => {
        sessionStorage.setItem('bookingSchedulesPreset', preset);
    }, [preset]);

    useEffect(() => {
        sessionStorage.setItem(
            'bookingSchedulesFilterByPeriodEnabled',
            String(filterByPeriodEnabled),
        );
    }, [filterByPeriodEnabled]);

    // Load data from API
    function loadBookingSchedules(params = { page: 1, rows: 20 }) {
        const { dateini, dateend, ...queryParams } = filters;
        if (filterByPeriodEnabled && queryParams) {
            queryParams.dateini = dateini;
            queryParams.dateend = dateend;
        }
        Get<any>(`/scs/${cxt?.sc?.id}/bookings/schedules`, {
            ...params,
            ...queryParams,
            ...presets[preset],

            trash,
        }).then(setBookingSchedules);
    }

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadBookingSchedules();
    });

    // On preset change, reload data
    useEffect(() => {
        loadBookingSchedules();
    }, []);

    // Init
    useEffect(() => {
        cxt?.setHeader({ title: t('FixedBookings') });
    }, []);

    // Go to /scs/bookings/schedules/create
    function goCreateNew() {
        navigate(getPath('bookingSchedule', { id: 'create' }));
    }

    return (
        <SttPage title={t('FixedBookings')}>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            type: 'text',
                            name: 'name',
                            value: filters.name,
                            onEnterKey: loadBookingSchedules,
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                        },
                    ] as any
                }
                extraFields={
                    [
                        {
                            caption: t('Status'),
                            type: 'select',
                            name: 'status',
                            value: preset,
                            // These options correspond to the presets declared above
                            options: [
                                {
                                    caption: t('All'),
                                    value: 'ALL',
                                },
                                {
                                    caption: t('Active'),
                                    value: 'ACTIVE',
                                },
                                {
                                    caption: t('OnTime'),
                                    value: 'ONTIME',
                                },
                            ],
                            onChange: ({
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => setPreset(value as string),
                        },
                        {
                            caption: `${t('dni')}/CIF`,
                            type: 'text',
                            name: 'dni',
                            value: filters.dni,
                        },
                        {
                            xs: 12,
                            sm: 12,
                            md: 6,
                            caption: t('Period'),
                            type: 'period',
                            enabled: filterByPeriodEnabled,
                            nameIni: 'dateini',
                            valIni: filters.dateini,
                            nameEnd: 'dateend',
                            valEnd: filters.dateend,
                            onToggle: () =>
                                setFilterByPeriodEnabled(
                                    !filterByPeriodEnabled,
                                ),
                        },
                    ] as any
                }
                onToggleTrash={toggleTrash}
                onChange={setFilter}
                onFilter={loadBookingSchedules}
            />
            <SttTable
                autoload={false}
                linkTo={(row: BookingSchedule) =>
                    getPath('bookingSchedule', { id: row.id })
                }
                onFetch={loadBookingSchedules}
                data={bookingSchedules}
                columns={[
                    {
                        title: '#',
                        type: 'tooltip',
                        value: (row: BookingSchedule) => ({
                            label: '#',
                            value: row.id,
                        }),
                        onClick: (row: BookingSchedule) =>
                            copyToClipboard(row.id, () =>
                                cxt?.showMessage('S', t('copiedToClipboard')),
                            ),
                    },
                    {
                        title: t('Name'),
                        align: 'left',
                        type: 'text',
                        value: (row: BookingSchedule) =>
                            row.name || t('Unknown'),
                    },
                    {
                        title: t('User'),
                        value: (row: BookingSchedule) => (
                            <SttChipUser user={{ login: fullName(row.user) }} />
                        ),
                    },
                    {
                        title: t('Active'),
                        type: 'binaryStatus',
                        value: (row: BookingSchedule) =>
                            row.status === 'ACTIVE',
                    },
                    {
                        title: t('Period'),
                        type: 'period',
                        value: (row: BookingSchedule) => ({
                            ini: row.dateini,
                            end: row.dateend,
                        }),
                    },
                    {
                        title: t('Facility'),
                        type: 'facility',
                        field: 'facility',
                    },
                    {
                        title: t('Bookings'),
                        type: 'text',
                        field: 'numPeriods',
                    },
                ]}
            />
            <SttButtonFab
                onClick={goCreateNew}
                permission={PERMISSIONS.FIXED_BOOKING.CREATE}
            />
        </SttPage>
    );
}
